import { capitalize, type Component, computed, nextTick } from "vue";

import { backendOld } from "~/api";
import { getPropertyConfig } from "~/common/properties";
import DocInfoSummary from "~/components/DocInfoSummary.vue";
import TaskDropdown from "~/components/docs/TaskDropdown.vue";
import ColorPicker from "~/components/dumb/ColorPicker.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DatePicker from "~/components/dumb/DatePicker.vue";
import DropdownDeleteMenuItem from "~/components/dumb/DropdownDeleteMenuItem.vue";
import ExportDropdown from "~/components/dumb/ExportDropdown.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import TaskUpdateDropdown from "~/components/dumb/TaskUpdateDropdown.vue";
import { copyAndNotify, notify } from "~/components/notifications";
import OptionTitleEditor from "~/components/options/OptionTitleEditor.vue";
import PagePropertiesEditorDartboardInfoSummary from "~/components/PagePropertiesEditorDartboardInfoSummary.vue";
import { USER_KIND_CUSTOM, USER_KIND_DART_AI, USER_KIND_USER } from "~/constants/userKinds";
import {
  AdminIcon,
  AgentIcon,
  AgentWorkflowIcon,
  AttachmentFieldIcon,
  AuthTokenIcon,
  ChangeIconIcon,
  ChevronLeftDoubleIcon,
  ChildRelationshipIcon,
  CopyIcon,
  DashboardIcon,
  DatesFieldIcon,
  DescriptionFieldIcon,
  DueDateFieldIcon,
  DuidFieldIcon,
  EditIcon,
  FavoriteIcon,
  FolderIcon,
  FullscreenIcon,
  GitBranchIcon,
  HideIcon,
  LinkIcon,
  LogOutIcon,
  MailIcon,
  NotificationIcon,
  OpenExternalLinkIcon,
  OutdentIcon,
  PlusIcon,
  PublicIcon,
  RefreshIcon,
  RenameIcon,
  RepeatIcon,
  ReplicateIcon,
  ReportsIcon,
  RestoreIcon,
  SettingsIcon,
  ShowIcon,
  SlashSquareIcon,
  SprintIcon,
  SprintRolloverIcon,
  TrashIcon,
  UndoIcon,
  UnfavoriteIcon,
  UpgradeRequiredIcon,
  UsersInviteIcon,
  UsersRemoveIcon,
} from "~/icons";
import {
  CommandId,
  DartboardKind,
  DropdownMenuItemKind,
  EditorMode,
  FolderKind,
  NotificationType,
  PageKind,
  Placement,
  SpaceKind,
  SprintMode,
  Subscription,
  TaskDetailMode,
  UserRole,
  UserStatus,
  ViewKind,
} from "~/shared/enums";
import type {
  Comment,
  Doc,
  DropdownMenuSection,
  Form,
  Option,
  Page,
  Property,
  SimpleDateRange,
  Skill,
  Task,
  TaskKind,
  User,
} from "~/shared/types";
import { useAppStore, useDataStore, useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { useUpgrade } from "~/utils/billing";
import {
  getDocLink,
  getPageDisplayName,
  getPublicViewLink,
  getReportsLink,
  getText,
  makeDartboardEmail,
} from "~/utils/common";
import { getOrdersBetween } from "~/utils/orderManager";
import { when } from "~/utils/wait";

import type { Actions } from ".";

const PAGE_KIND_TO_DELETE_DIALOG_DESCRIPTION = new Map([
  [
    PageKind.DARTBOARD,
    "Permanently deleting a dartboard will remove it for everyone in the workspace and move all its tasks to the trash. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.DASHBOARD,
    "Permanently deleting a dashboard will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.DOC,
    "Permanently deleting a doc will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.FOLDER,
    "Permanently deleting a folder will remove it and all of its docs for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.SPACE,
    "Permanently deleting a space will remove it and everything within it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
  [
    PageKind.VIEW,
    "Permanently deleting a view will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
  ],
]);
const PAGE_KINDS_WITHOUT_CONTEXT_MENUS = new Set([
  PageKind.DASHBOARDS_ROOT,
  PageKind.HOME,
  PageKind.INBOX,
  PageKind.VIEWS_ROOT,
]);
const OPTION_DELETE_DIALOG_DESCRIPTION =
  "Permanently deleting an option will remove it from any tasks it is on. This can't be undone. Are you sure you want to proceed?";

const ROTATE_AGENT_AUTH_TOKEN_DIALOG_DESCRIPTION =
  "Rotating this agent's authentication token will invalidate the old token and make a new one. This can't be undone. Are you sure you want to proceed?";
const DELETE_AGENT_AUTH_TOKEN_DIALOG_DESCRIPTION =
  "Deleting this agent's authentication token will invalidate the old token. This can't be undone. Are you sure you want to proceed?";

let LinkToRelationshipsDropdown: Component;
let OptionEditDropdown: Component;

/** Context menu actions */
export default (actions: Actions) => ({
  init(linkToRelationshipsDropdown: Component, optionEditDropdown: Component) {
    LinkToRelationshipsDropdown = linkToRelationshipsDropdown;
    OptionEditDropdown = optionEditDropdown;
  },
  comment(comment: Comment, emit: (e: string) => void) {
    const environmentStore = useEnvironmentStore();
    const userStore = useUserStore();

    const items = [
      {
        title: "Copy link",
        kind: DropdownMenuItemKind.BUTTON,
        icon: LinkIcon,
        onClick: () => {
          const text = getText(comment.text);
          const title = text.length > 50 ? `${text.slice(0, 50)}…` : text;
          const url = environmentStore.getCommentUrl(comment);
          copyAndNotify("Link to comment", url, `<a href=${url}>${title || "(no title)"}</a>`);
        },
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("ID of comment", comment.duid),
      },
    ];

    if (comment.authorDuid === userStore.duid) {
      items.push({
        title: "Edit",
        kind: DropdownMenuItemKind.BUTTON,
        icon: EditIcon,
        onClick: () => emit("startEdit"),
      });
      items.push({
        title: "Delete",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TrashIcon,
        onClick: () => emit("delete"),
      });
    }

    return [
      {
        title: "Options",
        items,
      },
    ];
  },
  doc(
    doc: Doc,
    options: { fullEdit?: boolean; startEditingTitle?: () => void; close?: (() => void) | undefined } = {}
  ) {
    // TODO deduplicate with section below that has the page context menu with a doc section
    const { fullEdit, startEditingTitle, close } = options;
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const pageStore = usePageStore();
    const userStore = useUserStore();
    const router = dataStore.$router;

    return [
      {
        title: "Actions",
        items: [
          {
            title: "Open doc",
            kind: DropdownMenuItemKind.BUTTON,
            icon: FullscreenIcon,
            hidden: appStore.docOpenInFullscreen?.duid === doc.duid,
            onClick: async () => {
              await dataStore.getDocByDuid(doc.duid);
              await nextTick();
              router.push(getDocLink(doc));
            },
          },
        ],
      },
      {
        title: "Modify",
        items: [
          {
            title: "Rename",
            kind: DropdownMenuItemKind.BUTTON,
            hidden: !startEditingTitle,
            icon: RenameIcon,
            onClick: startEditingTitle,
          },
          {
            title: "Doc AI",
            kind: DropdownMenuItemKind.BUTTON,
            hidden:
              !pageStore.isMobile ||
              !appStore.docOpenInFullscreen ||
              !pageStore.isOnline ||
              !userStore.isRoleGte(UserRole.MEMBER),
            icon: SlashSquareIcon,
            onClick: () => appStore.doc?.openRecommendations(),
          },
          {
            title: "Link a task",
            kind: DropdownMenuItemKind.COMPONENT,
            hidden: !pageStore.isMobile,
            component: TaskDropdown,
            componentArgs: {
              doc,
            },
          },
          {
            title: "Change folder",
            kind: DropdownMenuItemKind.COMPONENT,
            component: GenericSubdropdown,
            componentArgs: {
              title: "Change folder",
              icon: FolderIcon,
              sections: dataStore.spaceList.map((space) => ({
                title: space.title,
                showTitle: true,
                items: dataStore.getFoldersBySpaceDuidOrdered(space.duid).map((folder) => ({
                  title: getPageDisplayName(folder, dataStore.getSpaceByDuid),
                  kind: DropdownMenuItemKind.BUTTON,
                  disabled: folder.duid === doc.folderDuid,
                  icon: PageIcon,
                  iconArgs: { page: folder },
                  onClick: () => dataStore.updateDocs([{ duid: doc.duid, folderDuid: folder.duid }]),
                })),
              })),
            },
          },
          {
            title: "Add attachment",
            kind: DropdownMenuItemKind.BUTTON,
            hidden: !fullEdit,
            icon: AttachmentFieldIcon,
            onClick: async () => {
              appStore.doc?.openFilePicker();
              close?.();
            },
          },
        ],
      },
      {
        title: "Copy",
        items: [
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => actions.doc.copyLinks([doc]),
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Doc ID", doc.duid),
          },
        ],
      },
      {
        title: "Meta",
        items: [
          {
            title: "Export as",
            kind: DropdownMenuItemKind.COMPONENT,
            component: ExportDropdown,
            componentArgs: {
              doc,
            },
          },
          {
            title: "Replicate doc",
            kind: DropdownMenuItemKind.BUTTON,
            icon: ReplicateIcon,
            onClick: async () => {
              const siblings = dataStore.getLoadedDocsByFolderDuidOrdered(doc.folderDuid);
              const prevSibling = siblings[siblings.findIndex((e) => e.duid === doc.duid) - 1];
              const order = getOrdersBetween(prevSibling?.order, doc.order)[0];

              const replicate = await dataStore.replicateDoc(doc, order);
              if (!replicate) {
                return;
              }
              router.push(getDocLink(replicate));
            },
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              title: `Delete ${getPageDisplayName(doc, dataStore.getSpaceByDuid)}`,
              description:
                "Permanently deleting a doc will remove it for everyone in the workspace. This can't be undone. Are you sure you want to proceed?",
              onDelete: () => dataStore.trashDoc(doc),
            },
          },
        ],
      },
      {
        title: "Doc Info",
        showTitle: false,
        items: [
          {
            title: "",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DocInfoSummary,
            componentArgs: {
              doc,
            },
            noFocus: true,
          },
        ],
      },
    ];
  },
  form(form: Form, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Open preview",
            kind: DropdownMenuItemKind.EXTERNAL_LINK,
            icon: OpenExternalLinkIcon,
            navigate: {
              to: environmentStore.getFormUrl(form),
              newTab: true,
            },
          },
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => copyAndNotify("Link to form", environmentStore.getFormUrl(form)),
          },
          {
            title: "Copy settings link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: SettingsIcon,
            onClick: () => {
              const fullUrl = environmentStore.getFormSettingsUrl(form.duid);
              copyAndNotify("Link to form settings", fullUrl);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Form ID", form.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  option(
    option: Option,
    options: {
      showTitleEditor?: boolean;
      property?: Property;
      addSubitem?: () => void;
      onRename?: (newTitle: string) => void;
      onChangeColor?: (newColorHex: string) => void;
    } = {}
  ) {
    const { showTitleEditor, property, addSubitem, onRename, onChangeColor } = options;
    const dataStore = useDataStore();
    const userStore = useUserStore();

    const propertyNorm = property ?? dataStore.getPropertyByDuid(option.propertyDuid);

    return [
      {
        title: "Title",
        items: [
          {
            title: onRename ? "Add" : "Rename",
            kind: DropdownMenuItemKind.COMPONENT,
            noFocus: true,
            hidden: showTitleEditor,
            component: OptionTitleEditor,
            componentArgs: {
              option,
              property: propertyNorm,
              adding: onRename !== undefined,
              onRename: (title: string) => {
                if (onRename) {
                  onRename(title);
                  return;
                }
                dataStore.updateOption({ duid: option.duid, title });
              },
            },
          },
        ],
      },
      {
        title: "Modify",
        items: [
          {
            title: "Change color",
            kind: DropdownMenuItemKind.COMPONENT,
            noFocus: true,
            component: ColorPicker,
            componentArgs: {
              value: option.colorHex,
              onSelect: (colorHex: string) => {
                if (onChangeColor) {
                  onChangeColor(colorHex);
                  return;
                }
                dataStore.updateOption({ duid: option.duid, colorHex });
              },
            },
          },
        ],
      },
      {
        title: "Meta",
        items: [
          {
            title: "Add sub-item",
            kind: addSubitem ? DropdownMenuItemKind.BUTTON : DropdownMenuItemKind.COMPONENT,
            hidden: !!onRename,
            icon: ChildRelationshipIcon,
            onClick: addSubitem,
            component: addSubitem ? undefined : OptionEditDropdown,
            componentArgs: addSubitem
              ? undefined
              : {
                  option,
                  isSubitemDropdownMenu: true,
                },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Option ID", option.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            hidden: !userStore.isRoleGte(UserRole.ADMIN),
            component: DropdownDeleteMenuItem,
            componentArgs: {
              title: `Delete ${option.title}`,
              description: OPTION_DELETE_DIALOG_DESCRIPTION,
              onDelete: () => dataStore.deleteOption(option),
            },
          },
        ],
      },
    ];
  },
  page(page: Page, showDescriptionOption: boolean, emit: (e: string) => void) {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    const isMemberOrGreater = userStore.isRoleGte(UserRole.MEMBER);
    const hasEditAccess = userStore.hasAccessToPageEditing();
    const isWorkspaceOrPersonal = page.kind === SpaceKind.WORKSPACE || page.kind === SpaceKind.PERSONAL;

    if (PAGE_KINDS_WITHOUT_CONTEXT_MENUS.has(page.pageKind)) {
      return [];
    }

    const renameItem = {
      title: "Rename",
      kind: DropdownMenuItemKind.BUTTON,
      icon: RenameIcon,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      onClick: () => emit("startEditingTitle"),
    };

    const changeIconItem = {
      title: "Change icon",
      kind: DropdownMenuItemKind.BUTTON,
      icon: ChangeIconIcon,
      hidden: !hasEditAccess,
      onClick: () => emit("startEditingIcon"),
    };

    const replicatePageItem = computed(() => ({
      title: `Replicate ${page.pageKind}`,
      kind: DropdownMenuItemKind.BUTTON,
      icon: ReplicateIcon,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      dataTestid: "dropdown-replicate-page",
      onClick: () => dataStore.replicatePage(page),
    }));

    const manageSharingItem = {
      title: "Manage sharing",
      kind: DropdownMenuItemKind.BUTTON,
      icon: UsersInviteIcon,
      hidden: !hasEditAccess || !(page.pageKind === PageKind.VIEW || page.pageKind === PageKind.DASHBOARD),
      onClick: () => {
        if (!page || !(page.pageKind === PageKind.VIEW || page.pageKind === PageKind.DASHBOARD)) {
          return;
        }
        appStore.setPageInPermissionsModal(page);
      },
    };

    const editDescriptionItem = {
      title: "Edit description",
      kind: DropdownMenuItemKind.BUTTON,
      icon: DescriptionFieldIcon,
      hidden: !hasEditAccess || !showDescriptionOption || pageStore.isMobile,
      onClick: () => emit("startEditingDescription"),
    };

    const pageName = getPageDisplayName(page, dataStore.getSpaceByDuid);
    const pageUrl = environmentStore.getPageUrl(page);
    const copyLinkItem = {
      title: "Copy link",
      kind: DropdownMenuItemKind.BUTTON,
      icon: LinkIcon,
      onClick: () =>
        copyAndNotify(`Link to ${page.pageKind}`, pageUrl, `<a href=${pageUrl}>${pageName || "(no title)"}</a>`),
    };

    const copyIdItem = {
      title: "Copy ID",
      kind: DropdownMenuItemKind.BUTTON,
      icon: DuidFieldIcon,
      onClick: () => copyAndNotify(`${capitalize(page.pageKind)} ID`, page.duid),
    };

    const exportItem = {
      title: "Export as",
      kind: DropdownMenuItemKind.COMPONENT,
      component: ExportDropdown,
      componentArgs: {
        page,
      },
    };

    const deleteItem = {
      title: "Delete",
      kind: DropdownMenuItemKind.COMPONENT,
      component: DropdownDeleteMenuItem,
      hidden: !hasEditAccess && page.pageKind !== PageKind.DOC,
      disabled:
        page.pageKind === PageKind.DARTBOARD &&
        page.spaceDuid === dataStore.workspaceSpace?.duid &&
        dataStore.getDartboardsBySpaceDuidOrdered(page.spaceDuid).length === 1,
      disabledReason: "You can't delete the only dartboard in General",
      componentArgs: {
        title: `Delete ${pageName}`,
        description: PAGE_KIND_TO_DELETE_DIALOG_DESCRIPTION.get(page.pageKind),
        onDelete: () => dataStore.deletePage(page),
      },
    };

    switch (page.pageKind) {
      case PageKind.DARTBOARD: {
        const infoSection = {
          title: "Info",
          items: [
            {
              title: "Info",
              kind: DropdownMenuItemKind.COMPONENT,
              component: PagePropertiesEditorDartboardInfoSummary,
              componentArgs: {
                dartboard: page,
              },
              noFocus: true,
            },
          ],
        };

        const resetRolloverItemMaybe =
          dataStore
            .getDartboardsByKindOrdered(DartboardKind.FINISHED)
            .find(({ spaceDuid }) => spaceDuid === page.spaceDuid)?.duid === page.duid
            ? [
                {
                  title: "Reset to Active",
                  kind: DropdownMenuItemKind.BUTTON,
                  icon: UndoIcon,
                  onClick: () => emit("resetToActive"),
                },
              ]
            : [];

        const rolloverItem = {
          title: `Start ${page.kind === DartboardKind.NEXT ? "this" : "next"} sprint`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: SprintRolloverIcon,
          onClick: () => emit("startNextSprint"),
        };

        const configureSprintsItem = {
          title: "Sprint settings",
          kind: DropdownMenuItemKind.BUTTON,
          icon: SprintIcon,
          hidden: !hasEditAccess,
          onClick: () => emit("startEditingSpace"),
        };

        const planFinishItem = {
          title: "Plan finish",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DatePicker,
          componentArgs: {
            value: page.plannedFinishAt,
            editorMode: EditorMode.CONTEXT_MENU,
            titleOverride: "Plan finish",
            iconOverride: DueDateFieldIcon,
            hideActions: true,
            placement: Placement.RIGHT_TOP,
            onSelect: (date: string | null) => dataStore.updateDartboard({ duid: page.duid, plannedFinishAt: date }),
          },
          icon: DueDateFieldIcon,
        };

        const activeDartboard = dataStore
          .getDartboardsBySpaceDuidOrdered(page.spaceDuid)
          .find((e) => e.kind === DartboardKind.ACTIVE);

        const planStartAndFinishItem = {
          title: "Plan start and finish",
          kind: DropdownMenuItemKind.COMPONENT,
          component: DatePicker,
          componentArgs: {
            value: { start: activeDartboard?.plannedFinishAt ?? null, end: page.plannedFinishAt },
            editorMode: EditorMode.CONTEXT_MENU,
            titleOverride: "Plan start and finish",
            iconOverride: DatesFieldIcon,
            hideActions: true,
            placement: Placement.RIGHT_TOP,
            onSelectRange: ({ start, end }: SimpleDateRange) => {
              if (!activeDartboard) {
                return;
              }
              dataStore.updateDartboard({ duid: activeDartboard.duid, plannedFinishAt: start ?? null });
              dataStore.updateDartboard({ duid: page.duid, plannedFinishAt: end ?? null });
            },
          },
          icon: DatesFieldIcon,
        };

        const linksSection: DropdownMenuSection = {
          title: "Links",
          items: [
            {
              title: "Go to dashboards",
              kind: DropdownMenuItemKind.INTERNAL_LINK,
              icon: DashboardIcon,
              navigate: { to: { name: "dashboards" } },
            },
          ],
        };
        const space = dataStore.getSpaceByDuid(page.spaceDuid);
        if (space) {
          linksSection.items.push({
            title: "Go to reports",
            kind: DropdownMenuItemKind.INTERNAL_LINK,
            icon: ReportsIcon,
            navigate: { to: getReportsLink(space) },
          });
        }

        const copyDartboardEmailItem = {
          title: "Copy dartboard email",
          kind: DropdownMenuItemKind.BUTTON,
          icon: MailIcon,
          hidden: !tenantStore.emailIntegrationEnabled,
          onClick: () => copyAndNotify(`Email for ${pageName}`, makeDartboardEmail(page, environmentStore.isProd)),
        };

        switch (page.kind) {
          case DartboardKind.ACTIVE:
          case DartboardKind.NEXT: {
            return [
              infoSection,
              { title: "Modify", items: [editDescriptionItem] },
              {
                title: "Manage",
                items: [
                  rolloverItem,
                  configureSprintsItem,
                  ...(space?.rolloverRecurrence !== null
                    ? []
                    : page.kind === DartboardKind.ACTIVE
                      ? [planFinishItem]
                      : [planStartAndFinishItem]),
                ],
              },
              linksSection,
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [exportItem] },
            ];
          }
          case DartboardKind.BACKLOG: {
            return [
              { title: "Modify", items: [editDescriptionItem] },
              { title: "Manage", items: [configureSprintsItem] },
              linksSection,
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [exportItem] },
            ];
          }
          case DartboardKind.FINISHED: {
            return [
              infoSection,
              { title: "Modify", items: [editDescriptionItem] },
              { title: "Manage", items: [...resetRolloverItemMaybe, configureSprintsItem] },
              linksSection,
              { title: "Copy", items: [copyLinkItem, copyIdItem] },
              { title: "Meta", items: [exportItem, deleteItem] },
            ];
          }
          case DartboardKind.CUSTOM: {
            return [
              { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem] },
              linksSection,
              { title: "Copy", items: [copyLinkItem, copyDartboardEmailItem, copyIdItem] },
              { title: "Meta", items: [replicatePageItem.value, exportItem, deleteItem] },
            ];
          }
          default: {
            throw new Error("Unknown dartboard kind");
          }
        }
      }
      case PageKind.DASHBOARD: {
        const isFavorited = page.favoritedByUserDuids.includes(userStore.duid);
        const toggleFavoriteItem = {
          title: isFavorited ? "Unfavorite" : "Favorite",
          kind: DropdownMenuItemKind.BUTTON,
          icon: isFavorited ? FavoriteIcon : UnfavoriteIcon,
          onClick: () => {
            if (!page) {
              return;
            }
            dataStore.updateDashboardFavorite(page.duid, !isFavorited);
          },
        };

        return [
          { title: "Favorite", items: [toggleFavoriteItem] },
          { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem, manageSharingItem] },
          { title: "Copy", items: [copyLinkItem, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, deleteItem] },
        ];
      }
      case PageKind.DOC: {
        // TODO deduplicate with section above that has the doc-specific context menu
        return [
          {
            title: "Modify",
            items: [
              renameItem,
              {
                title: "Change folder",
                kind: DropdownMenuItemKind.COMPONENT,
                component: GenericSubdropdown,
                componentArgs: {
                  title: "Change folder",
                  icon: FolderIcon,
                  sections: dataStore.spaceList.map((space) => ({
                    title: space.title,
                    showTitle: true,
                    items: dataStore.getFoldersBySpaceDuidOrdered(space.duid).map((folder) => ({
                      title: getPageDisplayName(folder, dataStore.getSpaceByDuid),
                      kind: DropdownMenuItemKind.BUTTON,
                      disabled: folder.duid === page.folderDuid,
                      icon: PageIcon,
                      iconArgs: { page: folder },
                      onClick: () => dataStore.updateDocs([{ duid: page.duid, folderDuid: folder.duid }]),
                    })),
                  })),
                },
              },
            ],
          },
          { title: "Copy", items: [copyLinkItem, copyIdItem] },
          {
            title: "Meta",
            items: [
              {
                title: "Export as",
                kind: DropdownMenuItemKind.COMPONENT,
                component: ExportDropdown,
                componentArgs: {
                  doc: page,
                },
              },
              replicatePageItem.value,
              deleteItem,
            ],
          },
        ];
      }
      case PageKind.FOLDER: {
        if (page.kind === FolderKind.DEFAULT) {
          return [
            { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem] },
            { title: "Copy", items: [copyLinkItem, copyIdItem] },
            { title: "Meta", items: [replicatePageItem.value] },
          ];
        }

        return [
          { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem] },
          { title: "Copy", items: [copyLinkItem, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, deleteItem] },
        ];
      }
      case PageKind.SPACE: {
        return [
          {
            title: "Manage",
            items: [
              {
                title: "Start next sprint",
                kind: DropdownMenuItemKind.BUTTON,
                icon: SprintRolloverIcon,
                hidden: page.sprintMode === SprintMode.NONE,
                onClick: () => emit("startNextSprint"),
              },
              {
                title: "Rename",
                kind: DropdownMenuItemKind.BUTTON,
                icon: RenameIcon,
                hidden: !hasEditAccess || appStore.settingsModalOpen || isWorkspaceOrPersonal,
                onClick: () => emit("renameSpace"),
              },
              {
                title: "Space settings",
                kind: DropdownMenuItemKind.BUTTON,
                icon: SettingsIcon,
                dataTestid: "configure-space",
                hidden: !hasEditAccess || appStore.settingsModalOpen,
                onClick: () => emit("startEditingSpace"),
              },
              {
                title: "Manage sharing",
                kind: DropdownMenuItemKind.BUTTON,
                icon: UsersInviteIcon,
                hidden: !hasEditAccess || appStore.settingsModalOpen || isWorkspaceOrPersonal,
                onClick: () => emit("manageSharing"),
              },
            ],
          },
          {
            title: "Copy",
            items: [
              {
                title: "Copy settings link",
                kind: DropdownMenuItemKind.BUTTON,
                icon: LinkIcon,
                onClick: () => {
                  const fullUrl = environmentStore.getSpaceSettingsUrl(page.duid, "basics");
                  copyAndNotify("Link to space settings", fullUrl);
                },
              },
              copyIdItem,
            ],
          },
          {
            title: "Links",
            items: [
              {
                title: "Go to reports",
                kind: DropdownMenuItemKind.INTERNAL_LINK,
                icon: ReportsIcon,
                hidden: !isMemberOrGreater,
                navigate: { to: getReportsLink(page) },
              },
            ],
          },
          {
            title: "Meta",
            items: [
              replicatePageItem.value,
              ...(!(page.kind === SpaceKind.WORKSPACE || page.kind === SpaceKind.PERSONAL) ? [deleteItem] : []),
            ],
          },
        ];
      }
      case PageKind.VIEW: {
        const isFavorited = page.favoritedByUserDuids.includes(userStore.duid);
        const toggleFavoriteItem = {
          title: isFavorited ? "Unfavorite" : "Favorite",
          kind: DropdownMenuItemKind.BUTTON,
          icon: isFavorited ? FavoriteIcon : UnfavoriteIcon,
          onClick: () => {
            if (!page) {
              return;
            }
            dataStore.updateViewFavorite(page.duid, !isFavorited);
          },
        };

        if (page.kind === ViewKind.MY_TASKS) {
          return [
            { title: "Favorite", items: [toggleFavoriteItem] },
            { title: "Copy", items: [copyLinkItem, copyIdItem] },
            { title: "Meta", items: [replicatePageItem.value, exportItem] },
          ];
        }

        const copyLinkItems = page.public
          ? [
              {
                title: "Copy public link",
                kind: DropdownMenuItemKind.BUTTON,
                icon: PublicIcon,
                onClick: () => {
                  const publicUrl = getPublicViewLink(page);
                  copyAndNotify(
                    "Public link to view",
                    publicUrl,
                    `<a href="${publicUrl}">${pageName || "(no title)"}</a>`
                  );
                },
              },
              { ...copyLinkItem, title: "Copy internal link" },
            ]
          : [copyLinkItem];
        return [
          { title: "Favorite", items: [toggleFavoriteItem] },
          { title: "Modify", items: [renameItem, changeIconItem, editDescriptionItem, manageSharingItem] },
          { title: "Copy", items: [...copyLinkItems, copyIdItem] },
          { title: "Meta", items: [replicatePageItem.value, exportItem, deleteItem] },
        ];
      }
      default: {
        throw new Error(`Unknown page kind: ${page.pageKind}`);
      }
    }
  },
  property(property: Property, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const propertyConfig = getPropertyConfig(property.kind);
    const tenantStore = useTenantStore();
    const pageStore = usePageStore();
    const environmentStore = useEnvironmentStore();

    const items = [];

    if (tenantStore.isDart && !pageStore.adminHidden) {
      items.push({
        title: "Copy link to type",
        kind: DropdownMenuItemKind.BUTTON,
        icon: AdminIcon,
        onClick: () => {
          const fullUrl = environmentStore.getPropertySettingsByKindUrl(property.kind);
          copyAndNotify("Property type link", fullUrl);
        },
      });
    }

    items.push(
      {
        title: "Copy link",
        kind: DropdownMenuItemKind.BUTTON,
        icon: LinkIcon,
        onClick: () => {
          const fullUrl = environmentStore.getPropertySettingsUrl(property.duid);
          copyAndNotify("Property link", fullUrl);
        },
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Property ID", property.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.COMPONENT,
        component: DropdownDeleteMenuItem,
        disabled: propertyConfig.isDefault,
        componentArgs: {
          dialog: dialog ?? undefined,
        },
      }
    );

    return [
      {
        title: "Options",
        items,
      },
    ];
  },
  skill(skill: Skill, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Copy settings link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => {
              const fullUrl = environmentStore.getSkillSettingsUrl(skill.duid);
              copyAndNotify("Link to skill settings", fullUrl);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Skill ID", skill.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  task(
    targetTask: Task,
    editorMode: EditorMode,
    tasks?: Task[],
    shouldSelectTask = true,
    close: (() => void) | undefined = undefined
  ): DropdownMenuSection[] {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    // Setup to get selected tasks
    const visualization = appStore.getActiveVisualization();

    const rowIsSelected = appStore.selectedTaskDuids.has(targetTask.duid);
    const isListMode = editorMode === EditorMode.LIST;
    const isBoardMode = editorMode === EditorMode.BOARD;
    const isListMiniTcmMode = editorMode === EditorMode.LIST_MINI_TCM;
    const isListMiniNonRoadmapMode = editorMode === EditorMode.LIST_MINI || isListMiniTcmMode;

    if (shouldSelectTask) {
      if (isListMiniNonRoadmapMode) {
        appStore.getSubtasksListVisualization()?.selectAndScrollTo(targetTask.duid, { deselectOthers: !rowIsSelected });
      } else {
        actions.visualization.selectRowByIdAndScroll(targetTask.duid, !rowIsSelected);
      }
    }

    // Task variables
    const selectedTasks = dataStore.getTasksByDuidsIfLoaded(
      tasks ? tasks.map((e) => e.duid) : visualization.getSelected().map((e) => e.duid),
      { includeTrashed: true, includeDraft: true }
    );

    const selectedTaskDuids = selectedTasks.map((e) => e.duid);

    const isMultipleTasks = selectedTasks.length > 1;
    const plural = isMultipleTasks ? "s" : "";

    const userIsSubscriber = selectedTasks.every((e) => e.subscriberDuids.includes(userStore.duid));
    const isTrash = selectedTasks.every((e) => e.inTrash);

    const navigateToTask = async () => {
      actions.visualization.navigateToTask(targetTask.duid);
      await when(computed(() => appStore.selectedTaskDuids.has(targetTask.duid)));
    };

    // Dropdown menu sections
    const openTaskDetailSection: DropdownMenuSection = {
      title: "Actions",
      items: [
        {
          title: `Open task${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon:
            pageStore.isMobile || userStore.taskDetailMode !== TaskDetailMode.RIGHTBAR
              ? FullscreenIcon
              : ChevronLeftDoubleIcon,
          commandId: CommandId.OPEN_TASK,
          hidden: editorMode === EditorMode.DETAIL && appStore.taskOpenInDetail?.duid === targetTask.duid,
          disabled: isMultipleTasks || appStore.taskOpenInDetail?.duid === targetTask.duid,
          onClick: () => {
            if (isListMiniNonRoadmapMode) {
              actions.visualization.navigateToTask(targetTask.duid);
              return;
            }
            if (!rowIsSelected) {
              actions.visualization.navigateToTask(targetTask.duid);
              return;
            }
            appStore.setTaskDetailOpen(true);
          },
        },
        {
          title: `Convert to normal task${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          disabled: isMultipleTasks,
          icon: OutdentIcon,
          hidden: !isListMiniNonRoadmapMode,
          onClick: () => actions.visualization.convertToNormalTask(targetTask),
        },
      ],
    };

    const copySection: DropdownMenuSection = {
      title: "Copy",
      items: [
        {
          title: `Copy link${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: LinkIcon,
          commandId: CommandId.COPY_LINK,
          onClick: async () => actions.visualization.copyTaskLinks(selectedTasks),
        },
        {
          title: `Copy branch name${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: GitBranchIcon,
          hidden: !tenantStore.githubIntegrationEnabled,
          commandId: CommandId.COPY_BRANCH,
          onClick: async () => actions.visualization.copyTaskBranchNames(selectedTasks),
        },
        {
          title: `Copy ID${plural}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: DuidFieldIcon,
          onClick: () =>
            copyAndNotify(`Task ID${selectedTaskDuids.length > 1 ? "s" : ""}`, selectedTaskDuids.join("\n")),
        },
      ],
    };

    const deleteItem = {
      title: "Delete",
      kind: DropdownMenuItemKind.BUTTON,
      icon: TrashIcon,
      commandId: CommandId.DELETE_TASK,
      dataTestid: "dropdown-delete-task",
      onClick: async () => actions.visualization.trashTasks(selectedTasks),
    };

    if (isListMiniTcmMode) {
      return [
        {
          title: "Meta",
          items: [deleteItem],
        },
      ];
    }

    if (isTrash) {
      return [
        openTaskDetailSection,
        copySection,
        {
          title: "Actions",
          items: [
            {
              title: "Restore",
              kind: DropdownMenuItemKind.BUTTON,
              icon: RestoreIcon,
              onClick: async () => actions.visualization.restoreTasks(),
            },
            {
              title: "Delete permanently",
              kind: DropdownMenuItemKind.BUTTON,
              commandId: CommandId.DELETE_TASK,
              icon: TrashIcon,
              onClick: async () => actions.visualization.deleteTasks(),
            },
          ],
        },
      ];
    }

    return [
      openTaskDetailSection,
      {
        title: "Modify",
        items: [
          {
            title: "Change task",
            kind: DropdownMenuItemKind.COMPONENT,
            component: TaskUpdateDropdown,
            componentArgs: {
              targetTask,
              selectedTasks,
            },
          },
          {
            title: "Connect a task, doc, and more",
            kind: DropdownMenuItemKind.COMPONENT,
            component: LinkToRelationshipsDropdown,
            componentArgs: {
              targetTask,
              selectedTasks,
            },
          },
          {
            title: "Add subtask",
            kind: DropdownMenuItemKind.BUTTON,
            icon: ChildRelationshipIcon,
            hidden: appStore.currentPage?.kind === DartboardKind.FINISHED,
            disabled: isMultipleTasks,
            commandId: CommandId.CREATE_SUBTASKS,
            onClick: async () => {
              if (!(isListMode || isBoardMode || isListMiniNonRoadmapMode) && !rowIsSelected) {
                await navigateToTask();
                actions.visualization.createSubtask(targetTask);
                return;
              }
              // Wait for the context menu to close
              // eslint-disable-next-line no-restricted-syntax
              setTimeout(() => {
                actions.visualization.createSubtask();
              }, 100);
            },
          },
          {
            title: "Add attachment",
            disabled: isMultipleTasks,
            kind: DropdownMenuItemKind.BUTTON,
            icon: AttachmentFieldIcon,
            commandId: CommandId.ADD_ATTACHMENT,
            onClick: async () => {
              if (!rowIsSelected) {
                await navigateToTask();
              }
              appStore.setTaskDetailOpen(true);
              await nextTick();
              appStore.taskDetail?.openFilePicker();
              close?.();
            },
          },
          {
            title: userIsSubscriber ? "Unsubscribe" : "Subscribe",
            kind: DropdownMenuItemKind.BUTTON,
            icon: NotificationIcon,
            onClick: () => {
              if (userIsSubscriber) {
                dataStore.removeSubscribers(selectedTaskDuids, [userStore.duid]);
              } else {
                dataStore.addSubscribers(selectedTaskDuids, [userStore.duid]);
              }
            },
          },
        ],
      },
      copySection,
      {
        title: "Meta",
        items: [
          {
            title: "Export as",
            kind: DropdownMenuItemKind.COMPONENT,
            disabled: isMultipleTasks,
            component: ExportDropdown,
            componentArgs: {
              task: targetTask,
            },
          },
          {
            title: `Replicate task${plural}`,
            kind: DropdownMenuItemKind.BUTTON,
            icon: ReplicateIcon,
            commandId: CommandId.REPLICATE_TASK,
            onClick: () => actions.visualization.replicateTasks(selectedTasks),
          },
          deleteItem,
        ],
      },
    ];
  },
  taskKind(taskKind: TaskKind, dialog: InstanceType<typeof ConfirmationDialog> | null) {
    const environmentStore = useEnvironmentStore();

    return [
      {
        title: "Options",
        items: [
          {
            title: "Copy settings link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => {
              const fullUrl = environmentStore.getTaskKindSettingsUrl(taskKind.duid);
              copyAndNotify("Link to type settings", fullUrl);
            },
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify("Type ID", taskKind.duid),
          },
          {
            title: "Delete",
            kind: DropdownMenuItemKind.COMPONENT,
            component: DropdownDeleteMenuItem,
            disabled: taskKind.locked,
            componentArgs: {
              dialog: dialog ?? undefined,
            },
          },
        ],
      },
    ];
  },
  user(user: User, emit: (e: string) => void, hideAction?: boolean) {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const pageStore = usePageStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    const router = dataStore.$router;

    const allUsers = dataStore.getUserList({ excludeAgents: true, includeSpecialists: true });
    const userIsAdmin = userStore.isRoleGte(UserRole.ADMIN);
    const isAgent = user.kind !== USER_KIND_USER;

    const { getUpgradeDialogTitle, handleUpgradeClick } = useUpgrade(tenantStore);

    const rotateOrCreateAuthTokenInner = async () => {
      const { data } = await backendOld.agents.rotateAuthToken(user.duid);
      const { authToken } = data;
      // eslint-disable-next-line no-param-reassign
      user.authToken = authToken;
      copyAndNotify("Auth token", authToken);
    };

    const rotateOrCreateAuthToken = async () => {
      if (!user.authToken) {
        await rotateOrCreateAuthTokenInner();
        return;
      }

      appStore.setDeleteConfirmationModalOpen({
        title: "Rotate authentication token",
        description: ROTATE_AGENT_AUTH_TOKEN_DIALOG_DESCRIPTION,
        confirmText: "Proceed",
        cancelText: "Cancel",
        onDelete: () => rotateOrCreateAuthTokenInner(),
      });
    };

    const sections = [
      {
        title: "Actions",
        items: [
          {
            title: "Open user page",
            hidden: !hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: isAgent ? AgentIcon : UsersInviteIcon,
            onClick: () => emit("openUserPage"),
          },
        ],
      },
      {
        title: "Edit",
        items: [
          // Agent only items
          {
            title: "Rename",
            hidden: !isAgent || user.kind !== USER_KIND_CUSTOM || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: RenameIcon,
            onClick: () => emit("startEditing"),
          },
          {
            title: "Dart AI settings",
            hidden: user.kind !== USER_KIND_DART_AI || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: SettingsIcon,
            onClick: () => router.push(environmentStore.getSettingsUrl("dart-ai")),
          },
          {
            title: "Agent settings",
            hidden: !isAgent || user.kind === USER_KIND_DART_AI || user.kind === USER_KIND_CUSTOM || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: SettingsIcon,
            onClick: () => emit("openConfigModal"),
          },
          {
            title: "Edit workflows",
            hidden: !isAgent || user.kind !== USER_KIND_CUSTOM || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: AgentWorkflowIcon,
            onClick: () => emit("openWorkflowModal"),
          },
          // User only items
          {
            title: getUpgradeDialogTitle(Subscription.PREMIUM),
            hidden: isAgent || user.status !== UserStatus.PENDING_SUBSCRIPTION_UPGRADE,
            kind: DropdownMenuItemKind.BUTTON,
            icon: UpgradeRequiredIcon,
            onClick: () => handleUpgradeClick(Subscription.PREMIUM),
          },
          {
            title: "Resend invitation email",
            hidden: isAgent || user.status !== UserStatus.INVITED,
            kind: DropdownMenuItemKind.BUTTON,
            icon: RepeatIcon,
            onClick: async () => {
              await backendOld.workspace.reinviteUsers([user.duid]);
              notify({
                message: "Invitation sent",
                type: NotificationType.SUCCESS,
              });
            },
          },
        ],
      },
      {
        title: "Copy",
        items: [
          // Agent only items
          {
            title: "Authentication token",
            hidden: !isAgent || user.kind !== USER_KIND_CUSTOM,
            kind: DropdownMenuItemKind.COMPONENT,
            component: GenericSubdropdown,
            componentArgs: {
              title: "Authentication token",
              icon: AuthTokenIcon,
              widthPixels: 128,
              sections: [
                {
                  title: "Authentication token",
                  items: [
                    {
                      title: "Create",
                      hidden: !!user.authToken,
                      kind: DropdownMenuItemKind.BUTTON,
                      icon: PlusIcon,
                      onClick: () => rotateOrCreateAuthToken(),
                    },
                    {
                      title: "Copy",
                      hidden: !user.authToken,
                      kind: DropdownMenuItemKind.BUTTON,
                      icon: CopyIcon,
                      onClick: () => copyAndNotify("Authentication token", user.authToken ?? ""),
                    },
                    {
                      title: "Rotate",
                      hidden: !user.authToken,
                      kind: DropdownMenuItemKind.BUTTON,
                      icon: RefreshIcon,
                      onClick: () => rotateOrCreateAuthToken(),
                    },
                    {
                      title: "Delete",
                      hidden: !user.authToken,
                      kind: DropdownMenuItemKind.BUTTON,
                      icon: TrashIcon,
                      onClick: async () => {
                        appStore.setDeleteConfirmationModalOpen({
                          title: "Delete authentication token",
                          description: DELETE_AGENT_AUTH_TOKEN_DIALOG_DESCRIPTION,
                          confirmText: "Delete",
                          cancelText: "Cancel",
                          onDelete: () => {
                            // eslint-disable-next-line no-param-reassign
                            user.authToken = null;
                            backendOld.agents.deleteAuthToken(user.duid);
                          },
                        });
                      },
                    },
                  ],
                },
              ],
            },
          },
          // Common items
          {
            title: "Copy link",
            kind: DropdownMenuItemKind.BUTTON,
            icon: LinkIcon,
            onClick: () => copyAndNotify(isAgent ? "Link to agent" : "Link to user", environmentStore.getUserUrl(user)),
          },
          {
            title: "Copy ID",
            kind: DropdownMenuItemKind.BUTTON,
            icon: DuidFieldIcon,
            onClick: () => copyAndNotify(isAgent ? "Agent ID" : "User ID", user.duid),
          },
          {
            title: "Make login token",
            hidden: !tenantStore.isDart || pageStore.adminHidden || !userIsAdmin,
            kind: DropdownMenuItemKind.BUTTON,
            icon: AdminIcon,
            onClick: async () => {
              const loginToken = (await backendOld.auth.makeTeammateLoginTokenAdmin(user.duid)).data;
              copyAndNotify("Login token", loginToken);
            },
          },
        ],
      },
      {
        title: "Manage",
        items: [
          // Agent only items
          {
            title: user.status !== UserStatus.AGENT_DISABLED ? "Disable" : "Enable",
            hidden: !isAgent,
            kind: DropdownMenuItemKind.BUTTON,
            icon: user.status !== UserStatus.AGENT_DISABLED ? HideIcon : ShowIcon,
            onClick: () => {
              dataStore.updateAgent({
                duid: user.duid,
                status: user.status === UserStatus.AGENT_DISABLED ? UserStatus.ACTIVE : UserStatus.AGENT_DISABLED,
              });
            },
          },
          {
            title: "Delete",
            hidden: !isAgent || user.kind === USER_KIND_DART_AI || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: UsersRemoveIcon,
            onClick: () => emit("deleteAgent"),
          },
          // User only items
          {
            title: user.duid === userStore.duid ? "Leave workspace" : "Remove from workspace",
            hidden: isAgent || allUsers.length === 1 || (user.duid !== userStore.duid && !userIsAdmin) || hideAction,
            kind: DropdownMenuItemKind.BUTTON,
            icon: user.duid === userStore.duid ? LogOutIcon : UsersRemoveIcon,
            dataTestid: "remove-user",
            onClick: () => emit("removeUser"),
          },
        ],
      },
    ];

    return sections;
  },
});
